'use client';

import React, { useState } from 'react';
import Link from 'next/link';
import { useRouter } from 'next/navigation';
import { Star, ShoppingCart, Calendar, Check } from 'lucide-react';
import ProductImage from './ProductImage';

interface ProductCardProps {
  product: {
    id: string | number;
    slug?: string;
    name: string;
    price: number;
    oldPrice?: number | null;
    weight?: string;
    category?: string;
    image?: string;
    images?: string[];
    tagText?: string;
    tagColor?: string;
    foodPreference?: string;
    averageRating?: number;
    reviewCount?: number;
    farmSource?: string;
    showFarmSource?: boolean;
    productVariants?: Array<{
      name: string;
      price: DoubleRange;
      oldPrice?: number;
      description?: string;
      stock: number;
    }>;
  };
  onAddToCart: (product: any, selectedSubscription?: string) => void;
}

export default function ProductCard({ product, onAddToCart }: ProductCardProps) {
  const router = useRouter();
  const [isSubscribeMode, setIsSubscribeMode] = useState(false);
  const [subFrequency, setSubFrequency] = useState<'WEEKLY' | 'BIWEEKLY' | 'MONTHLY'>('WEEKLY');
  const [isHovered, setIsHovered] = useState(false);

  const {
    id,
    slug,
    name,
    price,
    oldPrice,
    weight = '1 Unit',
    category = 'Organic',
    image,
    images = [],
    tagText,
    tagColor,
    foodPreference,
    averageRating = 0.0,
    reviewCount = 0,
    farmSource,
    showFarmSource = true,
    productVariants
  } = product;

  const productSlug = slug || name.toLowerCase().replaceAll(/[^a-z0-9]+/g, '-').replaceAll(/^-|-$/g, '');

  // Eligible categories for subscription
  const lowerCat = category.toLowerCase();
  const isSubscriptionEligible = 
    lowerCat.includes('honey') || 
    lowerCat.includes('vegetable') || 
    lowerCat.includes('fruit');



  // Calculate savings from variants
  const firstVariant = productVariants && productVariants.length > 0 ? productVariants[0] : null;
  const currentPrice = firstVariant ? firstVariant.price : price;
  const currentOldPrice = firstVariant ? (firstVariant.oldPrice || oldPrice) : oldPrice;

  const savings = currentOldPrice && currentOldPrice > currentPrice ? Math.round(currentOldPrice - currentPrice) : 0;
  const discountedPrice = isSubscribeMode ? Math.round(currentPrice * 0.9) : currentPrice;

  // Determine stock of the first variant (or default to 10 if not defined)
  const firstVariantStock = productVariants && productVariants.length > 0
    ? productVariants[0].stock
    : (product as any).stock !== undefined ? (product as any).stock : 10;

  const handleAdd = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (firstVariantStock > 0) {
      onAddToCart(product, isSubscribeMode ? subFrequency : undefined);
    }
  };

  const handleCardClick = (e: React.MouseEvent) => {
    const target = e.target as HTMLElement;
    if (target.closest('button') || target.closest('input') || target.closest('a')) {
      return;
    }
    router.push(`/product/${productSlug}`);
  };

  return (
    <div 
      onClick={handleCardClick}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      className="bg-white rounded-[2rem] border border-border/60 p-4 sm:p-5 hover:shadow-xl hover:-translate-y-1 transition-all duration-300 group flex flex-col justify-between h-full relative overflow-hidden cursor-pointer select-none"
    >
      {/* Out of Stock Overlay */}
      {firstVariantStock <= 0 && (
        <div className="absolute inset-0 bg-black/60 z-20 flex items-center justify-center text-white font-bold text-sm tracking-wider uppercase">
          Out of Stock
        </div>
      )}

      {/* Visual Badge Tag */}
      {tagText ? (
        <span 
          className="absolute top-4 left-4 z-10 text-white px-2.5 py-0.5 rounded-lg text-[9px] font-black uppercase tracking-wider shadow-sm"
          style={{ backgroundColor: tagColor || '#1C3A2B' }}
        >
          {tagText}
        </span>
      ) : savings > 0 ? (
        <span className="absolute top-4 left-4 z-10 bg-amber text-deep-green px-2.5 py-0.5 rounded-lg text-[9px] font-black uppercase tracking-wider shadow-sm">
          Save ₹{savings}
        </span>
      ) : null}

      {/* Low stock badge */}
      {firstVariantStock > 0 && firstVariantStock <= 5 && (
        <span className="absolute top-4 right-4 z-10 bg-red-100 text-red-600 px-2 py-0.5 rounded text-[8px] font-black uppercase tracking-wider border border-red-200">
          Only {firstVariantStock} left!
        </span>
      )}

      {/* Veg/Non-Veg Visual Indicator */}
      {foodPreference === 'NON_VEG' && firstVariantStock > 5 && (
        <span className="absolute top-4 right-4 z-10 w-4.5 h-4.5 bg-white rounded border border-red-600 flex items-center justify-center">
          <span className="w-2 h-2 rounded-full bg-red-600"></span>
        </span>
      )}

      {/* Product Image */}
      <div 
        className="relative aspect-square w-full bg-surface rounded-2xl mb-4 overflow-hidden border border-border/50 block"
      >
        <ProductImage 
          src={isHovered && images.length > 1 ? images[1] : (image || images[0])} 
          alt={name} 
          className="absolute inset-0 w-full h-full object-cover group-hover:scale-102 transition-transform duration-500" 
        />
      </div>

      {/* Product Information */}
      <div className="flex-1 flex flex-col justify-between">
        <div>
          {/* Farm Source */}
          {farmSource && (
            <div className="flex justify-between items-center text-[10px] font-bold text-text-muted mb-1 uppercase tracking-wider">
              <span>{farmSource}</span>
            </div>
          )}

          {/* Product Name */}
          <div 
            className="font-display font-bold text-deep-green mb-1.5 line-clamp-2 text-sm sm:text-base md:text-lg leading-snug hover:text-mid-green transition-colors"
          >
            {name}
          </div>

          {/* Weight and Review stars */}
          <div className="flex items-center justify-between mb-3.5">
            <span className="text-xs text-text-muted font-bold">{weight}</span>
            
            {reviewCount > 0 ? (
              <div className="flex items-center gap-0.5">
                {[1, 2, 3, 4, 5].map((s) => (
                  <Star 
                    key={s} 
                    className={`w-3 h-3 ${s <= Math.round(averageRating) ? 'fill-amber text-amber' : 'text-gray-200'}`} 
                  />
                ))}
                <span className="text-[9px] text-text-muted ml-1 font-bold">({reviewCount})</span>
              </div>
            ) : null}
          </div>
        </div>

        {/* Subscription Segment */}
        {isSubscriptionEligible && firstVariantStock > 0 && (
          <div className="bg-surface rounded-2xl p-2.5 border border-border/60 mb-4 text-left" onClick={(e) => e.stopPropagation()}>
            <label className="flex items-center gap-2 cursor-pointer select-none">
              <input 
                type="checkbox" 
                checked={isSubscribeMode}
                onChange={() => setIsSubscribeMode(!isSubscribeMode)}
                className="w-4 h-4 rounded text-mid-green focus:ring-mid-green border-gray-300"
              />
              <span className="text-[10px] sm:text-xs font-bold text-deep-green">
                Subscribe & Save <span className="text-amber">10%</span>
              </span>
            </label>
            
            {isSubscribeMode && (
              <div className="flex gap-1.5 mt-2">
                {(['WEEKLY', 'BIWEEKLY', 'MONTHLY'] as const).map((freq) => (
                  <button
                    key={freq}
                    type="button"
                    onClick={() => setSubFrequency(freq)}
                    className={`flex-1 text-[9px] font-bold py-1 rounded-lg border transition-all ${
                      subFrequency === freq 
                        ? 'bg-deep-green border-deep-green text-white shadow-sm' 
                        : 'bg-white border-border text-text-muted hover:border-mid-green'
                    }`}
                  >
                    {freq === 'WEEKLY' ? 'Weekly' : freq === 'BIWEEKLY' ? '2 Weeks' : 'Monthly'}
                  </button>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Pricing & Checkout Actions */}
        <div className="flex justify-between items-center pt-3 sm:pt-4 border-t border-border/80 mt-auto" onClick={(e) => e.stopPropagation()}>
          <div className="flex flex-col text-left">
            {currentOldPrice && <span className="text-[10px] text-text-muted line-through mb-0.5 font-bold">₹{currentOldPrice}</span>}
            <span className="font-black text-base sm:text-lg md:text-xl text-deep-green leading-none">
              ₹{discountedPrice}
              {isSubscribeMode && <span className="text-[10px] text-amber ml-1 font-bold">/ delivery</span>}
            </span>
          </div>

          <button 
            disabled={firstVariantStock <= 0}
            onClick={handleAdd}
            className={`px-2 py-1.5 sm:px-5 sm:py-2.5 rounded-xl flex items-center justify-center gap-1.5 transition-all shadow-md group-hover:scale-[1.03] active:scale-95 text-[10px] sm:text-xs font-bold uppercase tracking-wider ${
              firstVariantStock <= 0
                ? 'bg-gray-300 text-gray-500 cursor-not-allowed shadow-none group-hover:scale-100'
                : 'bg-mid-green text-white hover:bg-deep-green shadow-mid-green/10'
            }`}
          >
            {firstVariantStock <= 0 ? (
              <span className="text-[9px] sm:text-xs">OOS</span>
            ) : isSubscribeMode ? (
              <>
                <Calendar className="w-3.5 h-3.5" /> <span className="hidden sm:inline">Subscribe</span>
              </>
            ) : (
              <>
                <ShoppingCart className="w-3.5 h-3.5" /> <span className="hidden sm:inline">Add</span>
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
}
